import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BTN, Field, INPUT } from "./maintUtils";

const P_FIELDS = [["name", "Entreprise *"], ["specialite", "Spécialité"], ["phone", "Téléphone standard"], ["phone_urgence", "Dépannage / urgence"], ["email", "Email"],
  ["website", "Site internet"], ["address", "Adresse"], ["contract_number", "N° de contrat"], ["horaires", "Horaires"], ["notes", "Notes"]];
const C_FIELDS = [["prenom", "Prénom"], ["nom", "Nom"], ["fonction", "Fonction"], ["phone", "Téléphone"], ["mobile", "Portable"], ["email", "Email"]];

export function ProviderDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ contacts: [], ...(initial || {}) }));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setC = (i, k, v) => set("contacts", f.contacts.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const save = async () => {
    try {
      const r = initial?.id ? await api.put(`/maint/providers/${initial.id}`, f) : await api.post("/maint/providers", f);
      toast.success("Prestataire enregistré"); onSaved(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}><DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-2xl" data-testid="prov-dialog">
      <DialogHeader><DialogTitle className="font-display text-xl">{initial?.id ? "Modifier le prestataire" : "Nouveau prestataire"}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-2">
        {P_FIELDS.map(([k, l]) => <Field key={k} label={l}><Input data-testid={`prov-${k}`} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className={INPUT} /></Field>)}
      </div>
      <div className="space-y-2 rounded-2xl bg-gray-50 p-3">
        <p className="text-xs font-black uppercase tracking-wider text-[#065F46]">Contacts / techniciens</p>
        {f.contacts.map((c, i) => (
          <div key={c.id || i} className="grid grid-cols-2 gap-2 border-b border-gray-100 pb-2 sm:grid-cols-[repeat(6,1fr)_24px]">
            {C_FIELDS.map(([k, l]) => <Input key={k} data-testid={`prov-contact-${k}-${i}`} value={c[k] || ""} onChange={(e) => setC(i, k, e.target.value)} placeholder={l} className="rounded-xl bg-white" />)}
            <button type="button" onClick={() => set("contacts", f.contacts.filter((_, j) => j !== i))} className="text-gray-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" data-testid="prov-add-contact" onClick={() => set("contacts", [...f.contacts, {}])} className="flex items-center gap-1 text-sm font-semibold text-[#065F46]"><Plus className="h-4 w-4" />Ajouter un contact</button>
      </div>
      <Button data-testid="prov-save" onClick={save} className={`w-full py-6 ${BTN}`}>Enregistrer</Button>
    </DialogContent></Dialog>
  );
}

const K_FIELDS = [["number", "N° de contrat", "text"], ["start", "Début", "date"], ["end", "Fin", "date"], ["renouvellement", "Renouvellement", "text"],
  ["cout_annuel", "Coût annuel (€)", "number"], ["delai", "Délai d'intervention prévu", "text"], ["sav_phone", "Téléphone SAV", "text"], ["inclus", "Interventions incluses", "text"]];

export function ContractDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ equipment_ids: [], ...(initial || {}) }));
  const [provs, setProvs] = useState([]);
  const [eqs, setEqs] = useState([]);
  const [file, setFile] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    api.get("/maint/providers").then((r) => setProvs(r.data)).catch(() => {});
    api.get("/maint/equipment").then((r) => setEqs(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    if (!f.provider_id) { toast.error("Choisissez le prestataire"); return; }
    try {
      const r = initial?.id ? await api.put(`/maint/contracts/${initial.id}`, f) : await api.post("/maint/contracts", f);
      if (file && f.equipment_ids[0]) {
        const fd = new FormData();
        fd.append("file", file); fd.append("equipment_id", f.equipment_ids[0]); fd.append("doc_type", "contrat"); fd.append("title", `Contrat ${f.number || ""}`);
        await api.post("/maint/documents", fd).catch((e) => toast.error(apiErr(e)));
      }
      toast.success("Contrat enregistré"); onSaved(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}><DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-2xl" data-testid="contract-dialog">
      <DialogHeader><DialogTitle className="font-display text-xl">Contrat de maintenance</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Prestataire *"><Select value={f.provider_id || undefined} onValueChange={(v) => set("provider_id", v)}><SelectTrigger data-testid="contract-provider" className={INPUT}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{provs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></Field>
        {K_FIELDS.map(([k, l, t]) => <Field key={k} label={l}><Input data-testid={`contract-${k}`} type={t} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={INPUT} /></Field>)}
        <Field label="Contrat PDF"><input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-2 w-full text-xs" /></Field>
      </div>
      <Field label="Appareils couverts">
        <div className="mt-1 flex flex-wrap gap-1.5">{eqs.map((e) => { const on = f.equipment_ids.includes(e.id); return (
          <button type="button" key={e.id} data-testid={`contract-eq-${e.id}`} onClick={() => set("equipment_ids", on ? f.equipment_ids.filter((x) => x !== e.id) : [...f.equipment_ids, e.id])} className={`rounded-full border px-2.5 py-1 text-xs ${on ? "border-[#065F46] bg-emerald-50 text-[#065F46]" : "border-gray-200 text-gray-500"}`}>{e.name}</button>
        ); })}</div>
      </Field>
      <Button data-testid="contract-save" onClick={save} className={`w-full py-6 ${BTN}`}>Enregistrer</Button>
    </DialogContent></Dialog>
  );
}
